import React from 'react';
import clsx from 'clsx';
import { TFunction, useTranslation } from 'next-i18next';
import Icon from '@components/atoms/icon/icon';
import { HotFilterItem } from '@interfaces/api/responses/hot-filters.response';
import { getTranslatedHotFilterName } from '@helpers/utils/hot-filters/hot-filters-parsing-util';
import HotFilterOptionItem from '../hot-filter-option-item/hot-filter-option-item';
import styles from './hot-filter-items.module.scss';

type HotFilterItemsProps = {
  childItem: HotFilterItem[];
  item: HotFilterItem;
  expanded: Record<string, boolean>;
  handleExpand: (id: string) => void;
  handleCheck: (childItem: HotFilterItem) => void;
  dataCy: string;
};

const getLabel = (child: HotFilterItem, t: TFunction): string => {
  if (child.name.startsWith('all_')) {
    return t(getTranslatedHotFilterName('all_items'));
  }
  return child?.name;
};

const getChildId = (item: HotFilterItem, child: HotFilterItem): string | undefined => {
  return item.name === 'color' ? child?.id : undefined;
};

const HotFilterItems: React.FC<HotFilterItemsProps> = ({
  dataCy,
  childItem,
  item,
  expanded,
  handleExpand,
  handleCheck,
}) => {
  const { t } = useTranslation();
  const { t: tA11y } = useTranslation('a11y');
  return (
    <ul
      className={clsx(styles.hotFilterItems__list)}
      data-cy={`${dataCy}-list`}
    >
      {childItem?.map((child, index) => (
        <li
          key={child?.id}
          className={clsx(styles.hotFilterItems__listItem, {
            [styles['hotFilterItems__listItem--hasChildren']]: child.children && child.children.length > 0,
          })}
          data-cy={`${dataCy}-list-item`}
        >
          {child?.name && (
            <>
              {child.children && child.children.length === 0 && (
                <HotFilterOptionItem
                  htmlId={`${child?.id}-${child?.name}-${index}`}
                  checked={child.isChecked || false}
                  onChange={() => handleCheck(child)}
                  label={getLabel(child, t)}
                  childId={getChildId(item, child)}
                  customLabelClass={styles.hotFilterItems__listItem__label}
                  identifier={`${dataCy}-list-item`}
                />
              )}
              {child.children && child.children.length > 0 && (
                <>
                  <div className={clsx(styles.hotFilterItems__listItem__mobileWrapper)}>
                    <details className={styles.hotFilterItems__listItem__details}>
                      <summary
                        className={clsx(styles.hotFilterItems__listItem__accordionSummary, {
                          [styles.hotFilterItems__listItem__accordionSummary__expanded]: expanded[child.id],
                        })}
                        onClick={() => handleExpand(child.id)}
                        data-cy={`${dataCy}-list-item-accordion${expanded[child.id] ? '-expanded' : ''}`}
                        aria-expanded={expanded[child.id]}
                      >
                        <legend
                          className={`${styles.hotFilterItems__listItem__accordionSummary__label} ${
                            child.children.filter((c) => c.isChecked).length > 0
                              ? styles['hotFilterItems__listItem__accordionSummary__label--selected']
                              : ''
                          }`}
                          data-cy={`${dataCy}-list-item-accordion-label${
                            child.children.filter((c) => c.isChecked).length
                              ? `-selected-${child.children.filter((c) => c.isChecked).length}`
                              : ''
                          }`}
                        >
                          {`${child.name} ${
                            child.children.filter((c) => c.isChecked && c.id !== `all_${c.parentID}`).length > 0
                              ? `(${child.children.filter((c) => c.isChecked && c.id !== `all_${c.parentID}`).length})`
                              : ''
                          }`}
                        </legend>
                        <Icon
                          className={styles.hotFilterItems__listItem__accordionSummary__arrowIcon}
                          name={expanded[child.id] ? 'ios-arrow-up-default' : 'ios-arrow-down-default'}
                          ariaLabel={
                            expanded[child.id] ? tA11y('A11Y.ICON.DROPDOWN_UP') : tA11y('A11Y.ICON.DROPDOWN_DOWN')
                          }
                        />
                      </summary>
                      {expanded[child.id] && child.children && (
                        <HotFilterItems
                          childItem={child.children}
                          item={item}
                          expanded={expanded}
                          handleExpand={handleExpand}
                          handleCheck={handleCheck}
                          dataCy={`${dataCy}-child`}
                        />
                      )}
                    </details>
                  </div>
                </>
              )}
            </>
          )}
        </li>
      ))}
    </ul>
  );
};

export default HotFilterItems;
